/**
 * HookHouse v4.0 Gemini Integration Service
 * 
 * Updated for local development:
 * - Uses Vite environment variables
 * - Maintains all existing functionality
 */

import { Type, generateText as geminiGenerateText } from '@/lib/gemini';
import { MUSIC_CRITIC_SYSTEM_PROMPT } from '@/constants';
import { IMAGE_BLUEPRINT_SYSTEM_PROMPT, extractBlock, buildSunoPromptInput, buildRegenerateInput, enforceLyricsLimit, trimTagList, buildSunoInjectionScript } from './llmShared';
import type { RegenerateOptions } from './llmShared';
import { HookhouseFormData, ParsedSunoOutput, MusicCritique, SunoClip } from '@/types';
import type { PersonaOverride } from '@/types/persona.types';
import { trackUsage } from '@/utils/costTracker';

// Atlas-grafted image blueprint prompt is the single source of truth in llmShared.ts
// (previously duplicated here as an obsolete pre-Atlas copy).

/**
 * Get AI client with proper API key handling
 * Priority: localStorage override > env variable
 */
const getGeminiApiKey = (): string => {
  const personalKey = localStorage.getItem('gemini_api_key');
  const apiKey = personalKey || '';
  if (!apiKey) {
    console.warn('No Gemini API key found. Add your key in Settings.');
  }
  return apiKey;
};

/**
 * Get model name with localStorage override
 */
const getModelName = (defaultModel: string) => {
  return localStorage.getItem('gemini_model') || defaultModel;
};

/**
 * Parse Gemini response into structured output
 */
const parseResponse = (responseText: string, styleStrength: number = 85, weirdness: number = 65): ParsedSunoOutput => {
  const fullResponse = responseText;
  const titleArtist = extractBlock(fullResponse, 'TITLE / ARTIST:');
  const summary = extractBlock(fullResponse, 'SUMMARY:');
  const styleBlueprint = extractBlock(fullResponse, 'STYLE (PRODUCTION BLUEPRINT):');
  const style = extractBlock(fullResponse, 'SUNO TAGS:');
  const excludedStyle = extractBlock(fullResponse, 'EXCLUDED STYLES:');
  const lyricsWithTags = extractBlock(fullResponse, 'LYRICS:');

  if (!titleArtist || !summary || !styleBlueprint || !lyricsWithTags) {
    throw new Error(`The session architect (Gemini) returned a malformed blueprint. Please kick the jukebox and try again.`);
  }

  // Split title // artist with fallback separators: //, /, —, –, " - "
  const titleSep = titleArtist.includes('//') ? '//'
    : titleArtist.includes(' — ') ? ' — '
    : titleArtist.includes(' – ') ? ' – '
    : titleArtist.includes(' - ') ? ' - '
    : '/';
  const parts = titleArtist.split(titleSep).map(s => s.trim());
  const parsed: ParsedSunoOutput = {
    titleArtist, summary, styleBlueprint, style, excludedStyle, lyricsWithTags,
    title: parts[0] || "Untitled", artist: parts[1] || "Unknown Artist",
    lyricsAlone: (lyricsWithTags || "").replace(/\{LAYER-1 VOCAL PHYSICS\}[\s\S]*?\n\n/g, '').replace(/\[.*?\]\n?/g, '').trim(),
    javascriptCode: "", fullResponse,
    styleStrength, weirdness
  };

  // The LLM is told to keep style/excluded-style tags under 1,000 chars, but
  // genre-blended briefs especially tend to overrun that limit. Trim to whole
  // tags here so the stored value matches what Suno actually receives instead
  // of silently exceeding it (this is what was rejecting vault saves for
  // tracks whose tags ran 1,005-1,342 chars).
  if ((parsed.style || '').length > 1000) {
    console.warn(`⚠️ [Gemini] Style tags exceed Suno limit: ${parsed.style.length}/1000 chars — trimming to whole tags`);
    parsed.style = trimTagList(parsed.style, 1000);
  }
  if ((parsed.excludedStyle || '').length > 1000) {
    console.warn(`⚠️ [Gemini] Excluded styles exceed Suno limit: ${parsed.excludedStyle.length}/1000 chars — trimming to whole tags`);
    parsed.excludedStyle = trimTagList(parsed.excludedStyle, 1000);
  }

  // Console injection script for manual Suno sync
  parsed.javascriptCode = buildSunoInjectionScript(
    parsed.title,
    parsed.style || parsed.styleBlueprint,
    parsed.lyricsWithTags || '',
    weirdness,
    styleStrength,
  );

  return parsed;
};

/**
 * Generate Suno prompt from form data
 */
export const generateSunoPrompt = async (
  data: HookhouseFormData,
  vocalArchitecture?: string,
  productionArchitecture?: string,
  riffDnaArchitecture?: string,
  soloArchitecture?: string,
  narrativeArchitecture?: string,
  personaOverride?: PersonaOverride | null
): Promise<ParsedSunoOutput> => {
  const apiKey = getGeminiApiKey();
  const model = getModelName('gemini-2.0-flash');

  const input = buildSunoPromptInput(
    data,
    vocalArchitecture,
    productionArchitecture,
    riffDnaArchitecture,
    soloArchitecture,
    narrativeArchitecture,
    personaOverride,
  );

  try {
    const { text, inputTokens, outputTokens } = await geminiGenerateText({
      apiKey, model, systemInstruction: input.systemPrompt,
      prompt: input.userPrompt, temperature: 0.85,
    });
    trackUsage({ provider: 'gemini', model, feature: 'generation', inputTokens, outputTokens });
    if (!text) throw new Error("No response from Gemini.");
    const parsed = parseResponse(text, data.styleStrength, data.weirdness);
    return await enforceLyricsLimit(parsed, async (sys, usr, temp, _max) => {
      // Adapter: Gemini's SDK shape → uniform LyricsTrimCallback shape.
      // maxTokens isn't honored at this call site (gemini-2.0-flash has
      // enough headroom for a single-block trim); the LLM uses the prompt's
      // ~4,700-char target instead.
      void _max;
      const r = await geminiGenerateText({
        apiKey, model, systemInstruction: sys, prompt: usr, temperature: temp,
      });
      return { text: r.text, inputTokens: r.inputTokens, outputTokens: r.outputTokens };
    });
  } catch (error: unknown) {
    console.error("Gemini API Error:", error);
    throw new Error(error instanceof Error ? error.message : "Unknown error generating prompt. Check your API key and quota.");
  }
};

/**
 * Regenerate with critique advice
 */
export const regenerateWithAdvice = async (originalOutput: ParsedSunoOutput, critique: MusicCritique, opts?: RegenerateOptions): Promise<ParsedSunoOutput> => {
  const apiKey = getGeminiApiKey();
  const model = getModelName('gemini-2.0-flash');

  const input = buildRegenerateInput(originalOutput, critique, opts);

  try {
    const { text, inputTokens, outputTokens } = await geminiGenerateText({
      apiKey, model, systemInstruction: input.systemPrompt,
      prompt: input.userPrompt, temperature: 0.8,
    });
    trackUsage({ provider: 'gemini', model, feature: 'generation', inputTokens, outputTokens });
    if (!text) throw new Error("No response from Gemini.");
    return parseResponse(text, originalOutput.styleStrength, originalOutput.weirdness);
  } catch (error: unknown) {
    throw new Error(`Regeneration Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Generate image blueprint for cover art
 */
export const generateImageBlueprint = async (output: ParsedSunoOutput): Promise<string> => {
  const apiKey = getGeminiApiKey();
  const model = getModelName('gemini-2.0-flash');

  const content = `Based on song data, generate a high-fidelity image blueprint in JSON format.\n\nTITLE: ${output.title}\nARTIST: ${output.artist}\nSUMMARY: ${output.summary}\nPRODUCTION STYLE: ${output.styleBlueprint}\nLYRICS: ${output.lyricsAlone.substring(0, 1000)}`;

  try {
    // Schema-less JSON mode — the system prompt instructs Gemini to emit
    // raw JSON; downstream callers do their own parsing.
    const { text, inputTokens, outputTokens } = await geminiGenerateText({
      apiKey, model, systemInstruction: IMAGE_BLUEPRINT_SYSTEM_PROMPT,
      prompt: content, temperature: 0.7,
      responseMimeType: 'application/json',
    });
    trackUsage({ provider: 'gemini', model, feature: 'artwork', inputTokens, outputTokens });
    return text || "";
  } catch (error: unknown) {
    throw new Error(`Blueprint Generation Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Generate music critique
 */
export const generateMusicCritique = async (clip: SunoClip): Promise<MusicCritique> => {
  const apiKey = getGeminiApiKey();
  const model = getModelName('gemini-2.0-flash');

  const styleInfo = clip.originalData?.styleBlueprint || clip.metadata.tags || 'N/A';
  const lyricsInfo = clip.originalData?.lyricsWithTags || clip.metadata.prompt || 'N/A';
  const summaryInfo = clip.originalData?.summary || 'N/A';

  const content = `Analyze this song architecture and provide a detailed JSON critique. You MUST provide real scores and meaningful feedback for every section.

  TITLE: ${clip.title}
  PRODUCTION ARCHITECTURE: ${styleInfo}
  LYRICAL ARCHITECTURE: ${lyricsInfo}
  NARRATIVE SUMMARY: ${summaryInfo}`;

  try {
    const { text, inputTokens, outputTokens } = await geminiGenerateText({
      apiKey,
      model,
      systemInstruction: MUSIC_CRITIC_SYSTEM_PROMPT,
      prompt: content,
      temperature: 0.75,
      responseSchema: {
          type: Type.OBJECT,
          properties: {
            songId: { type: Type.STRING },
            timestamp: { type: Type.STRING },
            overallScore: { type: Type.NUMBER },
            sections: {
              type: Type.OBJECT,
              properties: {
                lyricalAnalysis: {
                  type: Type.OBJECT,
                  properties: {
                    score: { type: Type.NUMBER },
                    feedback: { type: Type.STRING },
                    strengths: { type: Type.ARRAY, items: { type: Type.STRING } },
                    weaknesses: { type: Type.ARRAY, items: { type: Type.STRING } }
                  },
                  required: ['score', 'feedback', 'strengths', 'weaknesses']
                },
                musicalArrangement: {
                  type: Type.OBJECT,
                  properties: {
                    score: { type: Type.NUMBER },
                    feedback: { type: Type.STRING },
                    instrumentationReview: { type: Type.STRING },
                    grooveAnalysis: { type: Type.STRING }
                  },
                  required: ['score', 'feedback', 'instrumentationReview', 'grooveAnalysis']
                },
                production: {
                  type: Type.OBJECT,
                  properties: {
                    score: { type: Type.NUMBER },
                    feedback: { type: Type.STRING },
                    mixQuality: { type: Type.STRING },
                    sonicClarity: { type: Type.STRING }
                  },
                  required: ['score', 'feedback', 'mixQuality', 'sonicClarity']
                },
                genreAuthenticity: {
                  type: Type.OBJECT,
                  properties: {
                    score: { type: Type.NUMBER },
                    feedback: { type: Type.STRING },
                    southernRockElements: { type: Type.ARRAY, items: { type: Type.STRING } },
                    authenticityConcerns: { type: Type.ARRAY, items: { type: Type.STRING } }
                  },
                  required: ['score', 'feedback', 'southernRockElements', 'authenticityConcerns']
                }
              },
              required: ['lyricalAnalysis', 'musicalArrangement', 'production', 'genreAuthenticity']
            },
        improvementSuggestions: { type: Type.ARRAY, items: { type: Type.STRING } },
        summaryVerdict: { type: Type.STRING }
      },
      required: ['overallScore', 'sections', 'improvementSuggestions', 'summaryVerdict']
      },
    });

    trackUsage({ provider: 'gemini', model, feature: 'critic', inputTokens, outputTokens });
    if (!text) throw new Error("No response from Gemini for critique.");
    const parsed = JSON.parse(text) as MusicCritique;

    return {
      ...parsed,
      songId: clip.id,
      timestamp: new Date().toISOString()
    };
  } catch (error: unknown) {
    console.error("Critique Error:", error);
    throw new Error(`Critique Generation Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};
